import IToolConfig from "../types/IToolConfig";
import addStylesheet from "./addStylesheet";

/**
 * Browser prefixes for CSS properties
 */
const BROWSER_PREFIXES = ['-o-', '-ms-', '-moz-', '-webkit-', ''];

/**
 * CSS properties that need vendor prefixes
 */
const PROPERTIES_NEEDING_PREFIX = ["filter"];

/**
 * Generates CSS string from styles object with vendor prefixes
 * @param styles - Object containing CSS properties and values
 * @returns Generated CSS string
 */
export function generateCSS(styles: Record<string, string>): string {
  if (!styles || typeof styles !== 'object') {
    return '';
  }

  let css = '';

  for (const [property, value] of Object.entries(styles)) {
    const prefixes = PROPERTIES_NEEDING_PREFIX.includes(property) 
      ? BROWSER_PREFIXES 
      : [''];
    
    prefixes.forEach((prefix: string) => {
      css += `${prefix}${property}:${value} !important;`;
    });
  }

  return css;
}

/**
 * Interface for CSS selector wrapping arguments
 */
export interface IWrapCSSToSelectorArgs {
  selector: string;
  childrenSelector?: string[];
  css: string;
}

/**
 * Wraps CSS with selectors
 * @param args - Object containing selector, children selector, and CSS
 * @returns Wrapped CSS string
 */
export function wrapCSSToSelector({ 
  selector,
  childrenSelector = [''],
  css
}: IWrapCSSToSelectorArgs): string {
  if (!selector || !css) {
    return '';
  }

  let output = '';

  childrenSelector.forEach(childSelector => {
    output += `${selector} ${childSelector}{${css}}`;
  });

  return output;
}

/**
 * Generates CSS from tool configuration
 * @param config - Tool configuration object
 * @returns Generated CSS string
 */
export function generateCSSFromConfig(config: IToolConfig): string {
  if (!config) {
    return '';
  }

  let output = '';

  // Generate CSS from styles
  output += generateCSS(config.styles || {});

  // Wrap with selector if CSS was generated and selector exists
  if (output.length && config.selector) {
    output = wrapCSSToSelector({
      selector: config.selector,
      childrenSelector: config.childrenSelector,
      css: output
    });
  }

  // Add additional CSS if provided
  if (config.css) {
    output += config.css;
  }

  return output;
}

/**
 * Injects or removes tool CSS based on configuration
 * @param config - Tool configuration object
 * @returns void
 */
export function injectToolCSS(config: IToolConfig): void {
  if (!config) {
    console.warn('No configuration provided to injectToolCSS');
    return;
  }

  const { id = '', enable = false } = config;
  const toolId = `cya11y-${id}`;

    if(enable) {
        try {
        let css = generateCSSFromConfig({...config, selector: `*:not(#cya11y-container):not(#cya11y-container *)`});
      
      if (css) {
        addStylesheet({
          css, 
          id: toolId
        });
      }
    } catch (error) {
      console.error(`Failed to inject CSS for tool ${id}:`, error);
    }
  } else {
    // Remove existing stylesheet if disabled
    const existingStylesheet = document.getElementById(toolId);
    if (existingStylesheet) {
      existingStylesheet.remove();
    }
  }

  // Toggle CSS class on document element
  document.documentElement.classList.toggle(toolId, enable);
}